/**
 * Every handling number in one place (design.md B4). Tune by feel, then
 * re-run `npm run laptime` to keep Desert City in its 18–22 s target.
 */

export const TICK_HZ = 120;
export const DT = 1 / TICK_HZ;

/** Display conversion: world pixels/second -> mph on the HUD. */
export const MPH_PER_PXS = 0.65;
export const pxsFromMph = (mph: number) => mph / MPH_PER_PXS;
export const mphFromPxs = (pxs: number) => pxs * MPH_PER_PXS;

/**
 * The original's team screen quotes 0–100 mph times of 6.5–8.2 s. Laps are
 * only ~20 s, so the game compresses time: real accel = quoted time × this.
 */
export const ACCEL_TIME_SCALE = 0.38;

export const BRAKE_DECEL = 620; // px/s²
export const COAST_DECEL = 55; // px/s² when off throttle and brake
export const REVERSE_ACCEL = 140; // px/s²
export const REVERSE_MAX = 55; // px/s

/** Steering: yaw rate in rad/s at full lock. */
export const MAX_YAW = 3.5;
/** Below this forward speed (px/s) steering fades out (no spinning on the spot). */
export const YAW_FULL_SPEED = 55;
/** Fraction of steering lost at top speed. */
export const HIGH_SPEED_STEER_LOSS = 0.32;

export const CAR_RADIUS = 6; // collision circle, px
export const CAR_LENGTH = 20;
export const CAR_WIDTH = 10;

/** A wall hit with more than this speed *into* the wall is a crash. */
export const CRASH_NORMAL_MPH = 60;
/** Below the crash threshold: bounce a little and lose speed. */
export const SCRAPE_RESTITUTION = 0.2;
export const SCRAPE_TANGENT_KEEP = 0.9;

/** Slipstream (design.md B4): close behind another car = less drag. */
export const SLIP_RANGE = 80; // px behind the car ahead
export const SLIP_LATERAL = 12; // px either side of its centre line
export const SLIP_BOOST = 0.05; // +5% top speed while in the tow

export const CRASH_RESPAWN_TICKS = Math.round(1.3 * TICK_HZ);

/** Tuning streak (design.md A7/B4): full throttle, no contact, 75% of a lap => +1 mph. */
export const STREAK_THROTTLE_MIN = 0.95;
export const STREAK_LAP_FRACTION = 0.75;
export const STREAK_MAX_BONUS_MPH = 5;

export type SurfaceId = 'tarmac' | 'kerb' | 'dust' | 'grass' | 'gravel' | 'carpark' | 'wall';

export interface SurfaceProps {
  /** Lateral grip: fraction of sideways velocity removed per second. */
  grip: number;
  /** Multiplier on the car's top speed while on this surface. */
  speedCap: number;
  /** Extra drag, px/s² per (px/s) of speed. */
  drag: number;
}

export const SURFACES: Record<Exclude<SurfaceId, 'wall'>, SurfaceProps> = {
  tarmac: { grip: 14, speedCap: 1.0, drag: 0 },
  kerb: { grip: 11.5, speedCap: 0.97, drag: 0.05 },
  dust: { grip: 7, speedCap: 0.75, drag: 0.35 },
  grass: { grip: 5, speedCap: 0.6, drag: 0.6 },
  gravel: { grip: 4, speedCap: 0.4, drag: 1.4 },
  carpark: { grip: 12.5, speedCap: 0.66, drag: 0.1 },
};
